import { cn } from 'lib/util/cn'
import Tooltip, { Side } from '../tooltip/tooltip'
import { InfoIcon } from 'lucide-react'
import { isNumber } from 'lodash'
import { cva } from 'class-variance-authority'
import { forwardRef } from 'react'

export interface Bar {
  progress: number
  color: string
}

interface ProgressBarProps {
  label?: string
  bars: Bar[]
  percentage?: number
  tooltipText?: string
  tooltipSide?: Side
  size?: 'sm' | 'md'
}

const bar = cva('tw-flex tw-w-full tw-rounded-full tw-bg-[#f3f3f3]', {
  variants: {
    size: {
      md: 'tw-h-2',
      sm: 'tw-h-1',
    },
  },
})

/**
 * @deprecated this has been migrated to the web app and any changes should be made in both the web app and here
 */
const ProgressBar = forwardRef<HTMLDivElement, ProgressBarProps>(
  ({ label, bars = [], percentage, tooltipText, tooltipSide, size = 'md', ...props }, ref) => {
    return (
      <div ref={ref} {...props}>
        {label && (
          <div className="tw-flex tw-items-center tw-justify-between">
            <span className="tw-text-sm tw-font-semibold tw-text-neutral-800">{label}</span>
            <div className="tw-flex tw-items-center tw-justify-center tw-gap-1 tw-text-xs tw-text-neutral-500">
              {isNumber(percentage) && <span>{percentage}% Done </span>}
              {tooltipText && (
                <Tooltip content={tooltipText}>
                  <div className="tw-pt-1">
                    <InfoIcon className="lu-xs tw-h-3 tw-w-3" />
                  </div>
                </Tooltip>
              )}
            </div>
          </div>
        )}
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
          <div className={bar({ size })}>
            {bars.map((bar, index) => (
              <div
                data-testid="progressbar"
                key={index}
                className={cn(
                  'tw-relative tw-top-0 tw-h-full first-of-type:tw-rounded-l-full',
                  bar.progress === 100 && 'tw-rounded-full',
                )}
                style={{ width: `${bar.progress}%`, backgroundColor: bar.color }}
              />
            ))}
          </div>
          {!label && (
            <div className="tw-flex tw-items-center tw-justify-center tw-gap-1 tw-text-nowrap tw-text-xs tw-text-neutral-500">
              {isNumber(percentage) && <span>{Math.round(percentage)}% Done</span>}
              {tooltipText && (
                <Tooltip content={tooltipText} side={tooltipSide}>
                  <InfoIcon className="lu-xs tw-h-3 tw-w-3" />
                </Tooltip>
              )}
            </div>
          )}
        </div>
      </div>
    )
  },
)

ProgressBar.displayName = 'ProgressBar'

export default ProgressBar
